import clsx from "clsx";
import { nanoid } from "nanoid";
import {
  MIME_TYPES,
  viewportCoordsToSceneCoords,
} from "@excalidraw/excalidraw";
import { newEmbeddableElement, newImageElement } from "@excalidraw/element/newElement";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { generateNKeysBetween } from "fractional-indexing";

import type {
  BinaryFileData,
  DataURL,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import type {
  FractionalIndex,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import { CloseIcon } from "@excalidraw/excalidraw/components/icons";
import { useCreatePortalContainer } from "@excalidraw/excalidraw/hooks/useCreatePortalContainer";
import {
  EMBEDDABLE_MEDIA_LINK_PREFIX,
  getEmbedLink,
} from "@excalidraw/element/embeddable";

import "./AIToolsPanel.scss";

const TOOLS_ENDPOINT =
  import.meta.env.VITE_AI_TOOLS_ENDPOINTS_URL ||
  "https://createstudio.app/api/fal-endpoints";
const GENERATE_ENDPOINT =
  import.meta.env.VITE_AI_TOOLS_GENERATE_URL ||
  "https://createstudio.app/api/fal-run";

const DRIVE_ORIGIN = "https://drive.createstudio.app";
const DRIVE_FILE_ENDPOINT = `${DRIVE_ORIGIN}/api/v1/drive-custom/`;

type SchemaType =
  | "textarea"
  | "text"
  | "number"
  | "select"
  | "boolean"
  | "file";

type ToolInputField = {
  key: string;
  label: string;
  type: SchemaType;
  required?: boolean;
  multiple?: boolean;
  options?: string;
  default?: string | number | boolean;
};

type RemoteTool = {
  id: number;
  endpoint_id: string;
  label: string;
  description?: string;
  cost?: string;
  category?: string;
  input_schema?: ToolInputField[];
};

type FieldValue = string | boolean | string[];

type StatusState =
  | { type: "success"; message: string }
  | { type: "error"; message: string }
  | { type: "info"; message: string }
  | null;

const isHttpUrl = (value: string) => /^https?:\/\//i.test(value);

const blobToDataURL = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to convert blob"));
    reader.readAsDataURL(blob);
  });

const readImageDimensions = (dataURL: string) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth || 1024, height: img.naturalHeight || 1024 });
    };
    img.onerror = () => reject(new Error("Unable to read generated image"));
    img.src = dataURL;
  });

const parseOptions = (options?: string) =>
  (options || "")
    .split(",")
    .map((opt) => opt.trim())
    .filter(Boolean);

const extractMediaUrls = (payload: any): string[] => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload.flatMap((entry) => extractMediaUrls(entry));
  }
  if (typeof payload === "string") {
    return isHttpUrl(payload) ? [payload] : [];
  }
  if (typeof payload === "object") {
    const urls: string[] = [];
    const candidateKeys = [
      "media_url",
      "mediaUrl",
      "media",
      "url",
      "urls",
      "output",
      "outputs",
      "images",
      "result",
      "results",
    ];

    candidateKeys.forEach((key) => {
      const value = payload[key];
      if (!value) {
        return;
      }
      if (typeof value === "string" && isHttpUrl(value)) {
        urls.push(value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === "string" && isHttpUrl(item)) {
            urls.push(item);
          } else if (item && typeof item === "object") {
            const nested = extractMediaUrls(item);
            urls.push(...nested);
          }
        });
      } else if (typeof value === "object") {
        urls.push(...extractMediaUrls(value));
      }
    });

    if (!urls.length && payload.data) {
      urls.push(...extractMediaUrls(payload.data));
    }

    return Array.from(new Set(urls)).filter(isHttpUrl);
  }
  return [];
};

const hasFileExtension = (url: string, extensions: string[]) => {
  const normalized = url.split(/[?#]/)[0]?.toLowerCase() || "";
  return extensions.some((extension) => normalized.endsWith(`.${extension}`));
};

const isProbablyImageUrl = (url: string) =>
  hasFileExtension(url, ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]);

const isProbablyVideoUrl = (url: string) =>
  hasFileExtension(url, ["mp4", "mov", "webm", "mkv", "avi", "m4v"]);

const isProbablyAudioUrl = (url: string) =>
  hasFileExtension(url, ["mp3", "wav", "aac", "flac", "m4a", "ogg"]);

const guessMimeTypeFromExtension = (
  url: string,
  mapping: Record<string, string>,
  fallback: string,
) => {
  const normalized = url.split(/[?#]/)[0]?.toLowerCase() || "";
  const extension = normalized.split(".").pop() || "";
  return mapping[extension] || fallback;
};

const guessAudioMimeType = (url: string) =>
  guessMimeTypeFromExtension(
    url,
    {
      mp3: "audio/mp3",
      wav: "audio/wav",
      aac: "audio/aac",
      flac: "audio/flac",
      m4a: "audio/mp4",
      ogg: "audio/ogg",
    },
    "audio/mp3",
  );

const guessVideoMimeType = (url: string) =>
  guessMimeTypeFromExtension(
    url,
    {
      mp4: "video/mp4",
      mov: "video/quicktime",
      webm: "video/webm",
      mkv: "video/x-matroska",
      avi: "video/x-msvideo",
      m4v: "video/x-m4v",
    },
    "video/mp4",
  );

const normalizeFieldValue = (field: ToolInputField): FieldValue => {
  switch (field.type) {
    case "boolean":
      return Boolean(field.default);
    case "number":
    case "text":
    case "textarea":
      return field.default !== undefined ? String(field.default) : "";
    case "select": {
      const opts = parseOptions(field.options);
      return (
        (field.default !== undefined ? String(field.default) : undefined) ||
        opts[0] ||
        ""
      );
    }
    case "file":
      return field.multiple ? [] : "";
    default:
      return "";
  }
};

const buildPayload = (
  tool: RemoteTool,
  values: Record<string, FieldValue>,
  email?: string | null,
) => {
  const inputs: Record<string, any> = {};
  (tool.input_schema || []).forEach((field) => {
    const rawValue = values[field.key];
    if (
      rawValue === undefined ||
      rawValue === "" ||
      (Array.isArray(rawValue) && rawValue.length === 0)
    ) {
      return;
    }
    if (field.type === "number") {
      const numeric = Number(rawValue);
      if (!Number.isNaN(numeric)) {
        inputs[field.key] = numeric;
      }
      return;
    }
    inputs[field.key] = rawValue;
  });
  if (email) {
    inputs.email = email;
  }
  return inputs;
};

const hasMissingRequiredFields = (
  tool: RemoteTool | null,
  values: Record<string, FieldValue>,
) => {
  if (!tool) {
    return true;
  }
  return (tool.input_schema || []).some((field) => {
    if (!field.required) {
      return false;
    }
    const value = values[field.key];
    if (field.type === "boolean") {
      return typeof value !== "boolean";
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === undefined || value === null || value === "";
  });
};

const getNextElementIndex = (
  elements: readonly OrderedExcalidrawElement[],
): FractionalIndex => {
  const lastIndex = elements.length
    ? elements[elements.length - 1].index
    : null;
  const [nextIndex] = generateNKeysBetween(lastIndex, null, 1);
  if (nextIndex) {
    return nextIndex as FractionalIndex;
  }
  const [fallback] = generateNKeysBetween(null, null, 1);
  return fallback as FractionalIndex;
};

export const AIToolsPanel = ({
  excalidrawAPI,
  isOpen,
  onClose,
  userEmail,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI;
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string | null;
}) => {
  const [tools, setTools] = useState<RemoteTool[]>([]);
  const [selectedToolId, setSelectedToolId] = useState<number | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, FieldValue>>({});
  const [search, setSearch] = useState("");
  const [loadingTools, setLoadingTools] = useState(false);
  const [toolsError, setToolsError] = useState<string | null>(null);
  const [hasLoadedTools, setHasLoadedTools] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [status, setStatus] = useState<StatusState>(null);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    try {
      const stored = localStorage.getItem("ai-tools-dock-collapsed");
      if (stored === null) {
        return true;
      }
      return stored === "true";
    } catch {
      return true;
    }
  });
  const portalContainer = useCreatePortalContainer({
    className: "ai-tools-dock-portal",
  });
  const hasInitializedCollapse = useRef(false);
  const [layoutAnimating, setLayoutAnimating] = useState(false);
  const [listAnimationKey, setListAnimationKey] = useState(0);
  const popupRef = useRef<Window | null>(null);
  const currentFieldKeyRef = useRef<string | null>(null);
  const fetchingToolsRef = useRef(false);
  const stopKeyboardPropagation = useCallback(
    (event: ReactKeyboardEvent) => {
      event.stopPropagation();
    },
    [],
  );

  const selectedTool = useMemo(() => {
    return tools.find((tool) => tool.id === selectedToolId) || null;
  }, [tools, selectedToolId]);

  const fieldMap = useMemo(() => {
    const map = new Map<string, ToolInputField>();
    (selectedTool?.input_schema || []).forEach((field) => map.set(field.key, field));
    return map;
  }, [selectedTool]);

  useEffect(() => {
    if (!isOpen || toolsError || hasLoadedTools || fetchingToolsRef.current) {
      return;
    }
    let cancelled = false;
    fetchingToolsRef.current = true;
    setLoadingTools(true);
    setToolsError(null);
    const controller = new AbortController();
    fetch(TOOLS_ENDPOINT, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Failed to load AI tools");
        }
        return response.json();
      })
      .then((data: RemoteTool[]) => {
        if (cancelled) {
          return;
        }
        const list = Array.isArray(data) ? data : [];
        setTools(list);
        setHasLoadedTools(true);
        setSelectedToolId((prev) => {
          if (prev && list.some((tool) => tool.id === prev)) {
            return prev;
          }
          return list[0]?.id ?? null;
        });
      })
      .catch((error) => {
        if (error.name === "AbortError" || cancelled) {
          return;
        }
        setToolsError(error.message || "Unable to fetch AI tools");
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTools(false);
        }
        fetchingToolsRef.current = false;
      });
    return () => {
      cancelled = true;
      controller.abort();
      fetchingToolsRef.current = false;
    };
  }, [hasLoadedTools, isOpen, toolsError]);

  const handleRetryLoadTools = useCallback(() => {
    fetchingToolsRef.current = false;
    setToolsError(null);
    setHasLoadedTools(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!hasInitializedCollapse.current) {
      setIsCollapsed(true);
      try {
        localStorage.setItem("ai-tools-dock-collapsed", "true");
      } catch {}
      hasInitializedCollapse.current = true;
    }
    try {
      localStorage.setItem("ai-tools-dock-collapsed", isCollapsed ? "true" : "false");
    } catch {}
  }, [isCollapsed, isOpen]);

  useEffect(() => {
    if (!selectedTool) {
      setFieldValues({});
      return;
    }
    const defaults: Record<string, FieldValue> = {};
    (selectedTool.input_schema || []).forEach((field) => {
      defaults[field.key] = normalizeFieldValue(field);
    });
    setFieldValues(defaults);
  }, [selectedTool]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== DRIVE_ORIGIN || event.data?.type !== "FILES_SELECTED") {
        return;
      }
      const fieldKey = currentFieldKeyRef.current;
      if (!fieldKey) {
        return;
      }
      const schema = fieldMap.get(fieldKey);
      if (!schema) {
        return;
      }
      const files = Array.isArray(event.data.files)
        ? event.data.files
        : [event.data];

      const urls = files
        .map((file: any) => `${DRIVE_FILE_ENDPOINT}${file.id}`)
        .filter(isHttpUrl);

      setFieldValues((prev) => ({
        ...prev,
        [fieldKey]: schema.multiple ? urls : urls[0] || "",
      }));
      popupRef.current?.close();
      popupRef.current = null;
      currentFieldKeyRef.current = null;
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [fieldMap, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStatus(null);
    }
  }, [isOpen]);

  const normalizedSearch = search.trim().toLowerCase();
  const filteredTools = useMemo(() => {
    if (!normalizedSearch) {
      return [];
    }
    return tools.filter((tool) => {
      const haystack = `${tool.label} ${tool.description || ""} ${tool.category || ""}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [normalizedSearch, tools]);
  const hasSearchQuery = Boolean(normalizedSearch);
  const visibleTools = useMemo(() => {
    if (hasSearchQuery) {
      return filteredTools;
    }
    return tools;
  }, [filteredTools, hasSearchQuery, tools]);
  const showNoResults = hasSearchQuery && filteredTools.length === 0;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLayoutAnimating(true);
    const timer = window.setTimeout(() => setLayoutAnimating(false), 400);
    return () => window.clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setListAnimationKey((prev) => prev + 1);
  }, [normalizedSearch, isOpen]);

  useEffect(() => {
    return () => {
      popupRef.current?.close();
      popupRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (visibleTools.length === 0) {
      if (hasSearchQuery && selectedToolId !== null) {
        setSelectedToolId(null);
      }
      return;
    }
    const existsInVisible = visibleTools.some((tool) => tool.id === selectedToolId);
    if (!existsInVisible) {
      setSelectedToolId(visibleTools[0].id);
    }
  }, [hasSearchQuery, isOpen, selectedToolId, visibleTools]);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleOpenDrivePicker = useCallback(
    (field: ToolInputField) => {
      try {
        const savedUser = localStorage.getItem("cs_user");
        const parsed = savedUser ? JSON.parse(savedUser) : null;
        const email = parsed?.email || "";
        const name = parsed?.name || "";
        if (!email && !name) {
          excalidrawAPI.setToast({
            message: "Please login to Createstudio before selecting files.",
            closable: true,
          });
          return;
        }
        const loginUrl = new URL(`${DRIVE_ORIGIN}/cs-login`);
        loginUrl.searchParams.set("email", email);
        loginUrl.searchParams.set("name", name);
        loginUrl.searchParams.set("type", "image");
        loginUrl.searchParams.set("multiple", field.multiple ? "true" : "false");

        const width = window.screen.width;
        const height = window.screen.height;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        popupRef.current = window.open(
          loginUrl.toString(),
          "cs-drive-picker",
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
        );
        currentFieldKeyRef.current = field.key;
      } catch (error) {
        console.warn("Failed to open drive picker", error);
        excalidrawAPI.setToast({
          message: "Unable to open drive picker. Please try again.",
          closable: true,
        });
      }
    },
    [excalidrawAPI],
  );

  const insertEmbeddableOnCanvas = useCallback(
    (link: string, index: number) => {
      const embedData = getEmbedLink(link);
      if (!embedData) {
        throw new Error(
          "Unable to embed generated media. Please download and insert it manually.",
        );
      }
      const width = embedData.intrinsicSize?.w ?? 560;
      const height = embedData.intrinsicSize?.h ?? 315;
      const appState = excalidrawAPI.getAppState();
      const center = viewportCoordsToSceneCoords(
        {
          clientX: appState.width / 2 + index * 20,
          clientY: appState.height / 2 + index * 20,
        },
        appState,
      );

      const embeddableElement = newEmbeddableElement({
        type: "embeddable",
        x: center.x - width / 2,
        y: center.y - height / 2,
        width,
        height,
        strokeColor: "transparent",
        backgroundColor: "transparent",
        fillStyle: appState.currentItemFillStyle,
        strokeWidth: appState.currentItemStrokeWidth,
        strokeStyle: appState.currentItemStrokeStyle,
        roughness: appState.currentItemRoughness,
        opacity: appState.currentItemOpacity,
        locked: false,
        link,
      });

      const existingElements =
        excalidrawAPI.getSceneElements() as OrderedExcalidrawElement[];
      const indexedElement: OrderedExcalidrawElement = {
        ...embeddableElement,
        index: getNextElementIndex(existingElements),
      };
      const nextElements = [...existingElements, indexedElement];
      excalidrawAPI.updateScene({
        elements: nextElements,
        appState: {
          ...appState,
          selectedElementIds: { [indexedElement.id]: true },
        },
      });
    },
    [excalidrawAPI],
  );

  const insertOutputOnCanvas = useCallback(
    async (url: string, index: number) => {
      const isLikelyImage = isProbablyImageUrl(url);
      const isLikelyAudio = isProbablyAudioUrl(url);
      const isLikelyVideo = isProbablyVideoUrl(url);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Unable to download generated media");
      }
      const blob = await response.blob();
      const mimeType = blob.type || "";
      const isImage =
        (mimeType && mimeType.startsWith("image/")) ||
        (!mimeType && isLikelyImage);

      if (isImage) {
        const dataURL = (await blobToDataURL(blob)) as DataURL;
        const { width, height } = await readImageDimensions(dataURL);
        const fileId = nanoid() as FileId;
        const file: BinaryFileData = {
          id: fileId,
          dataURL,
          mimeType: (mimeType || MIME_TYPES.png) as BinaryFileData["mimeType"],
          created: Date.now(),
          lastRetrieved: Date.now(),
        };
        excalidrawAPI.addFiles([file]);

        const appState = excalidrawAPI.getAppState();
        const center = viewportCoordsToSceneCoords(
          {
            clientX: appState.width / 2 + index * 20,
            clientY: appState.height / 2 + index * 20,
          },
          appState,
        );

        const imageElement = newImageElement({
          type: "image",
          x: center.x - width / 2,
          y: center.y - height / 2,
          width,
          height,
          strokeColor: appState.currentItemStrokeColor,
          backgroundColor: appState.currentItemBackgroundColor,
          fillStyle: appState.currentItemFillStyle,
          strokeWidth: appState.currentItemStrokeWidth,
          strokeStyle: appState.currentItemStrokeStyle,
          roughness: appState.currentItemRoughness,
          opacity: appState.currentItemOpacity,
          locked: false,
          roundness: null,
          fileId,
          status: "saved",
        });

        const existingElements =
          excalidrawAPI.getSceneElements() as OrderedExcalidrawElement[];
        const indexedElement: OrderedExcalidrawElement = {
          ...imageElement,
          index: getNextElementIndex(existingElements),
        };
        const nextElements = [...existingElements, indexedElement];
        excalidrawAPI.updateScene({
          elements: nextElements,
          appState: {
            ...appState,
            selectedElementIds: { [indexedElement.id]: true },
          },
        });
        return "image" as const;
      }

      const isAudio =
        (mimeType && mimeType.startsWith("audio/")) || isLikelyAudio;
      const isVideo =
        (mimeType && mimeType.startsWith("video/")) || isLikelyVideo;
      const isMedia = isAudio || isVideo;

      if (isMedia) {
        const guessedMimeType = mimeType
          ? mimeType
          : isAudio
            ? guessAudioMimeType(url)
            : guessVideoMimeType(url);
        const normalizedBlob = mimeType
          ? blob
          : new Blob([blob], { type: guessedMimeType });
        const dataURL = (await blobToDataURL(normalizedBlob)) as DataURL;
        insertEmbeddableOnCanvas(
          `${EMBEDDABLE_MEDIA_LINK_PREFIX}${dataURL}`,
          index,
        );
        return "embed" as const;
      }

      insertEmbeddableOnCanvas(url, index);
      return "embed" as const;
    },
    [excalidrawAPI, insertEmbeddableOnCanvas],
  );

  const handleGenerate = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!selectedTool || isGenerating) {
        return;
      }
      if (hasMissingRequiredFields(selectedTool, fieldValues)) {
        setStatus({
          type: "error",
          message: "Please fill all required fields before generating.",
        });
        return;
      }
      setIsGenerating(true);
      setStatus({ type: "info", message: "Starting generation..." });
      try {
        const payload = buildPayload(selectedTool, fieldValues, userEmail);
        const response = await fetch(GENERATE_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            endpoint_id: selectedTool.endpoint_id,
            inputs: payload,
            simulate:false,
            media_type: "image",
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || "Generation failed");
        }
        const urls = extractMediaUrls(data);
        if (!urls.length) {
          throw new Error("Generation finished but no media was returned.");
        }
        const imagePreviewUrls: string[] = [];
        for (const [index, url] of urls.entries()) {
          const result = await insertOutputOnCanvas(url, index);
          if (result === "image") {
            imagePreviewUrls.push(url);
          }
        }
        setPreviews(imagePreviewUrls);
        excalidrawAPI.setToast({
          message: `${urls.length} item(s) added to the canvas.`,
        });
        setStatus({
          type: "success",
          message: "Generation completed and added to the board.",
        });
      } catch (error: any) {
        const message = error?.message || "Unable to generate content.";
        setStatus({ type: "error", message });
      } finally {
        setIsGenerating(false);
      }
    },
    [
      excalidrawAPI,
      fieldValues,
      insertOutputOnCanvas,
      isGenerating,
      selectedTool,
      userEmail,
    ],
  );

  const handleClosePanel = useCallback(() => {
    popupRef.current?.close();
    popupRef.current = null;
    onClose();
  }, [onClose]);

  if (!isOpen || !portalContainer) {
    return null;
  }

  const searchInputId = "ai-tools-search-input";

  return createPortal(
    <section
      className={clsx("ai-tools-dock", { "ai-tools-dock--collapsed": isCollapsed })}
      aria-label="AI Tools"
    >
      <div
        className={clsx("ai-tools-dock__surface", {
          "ai-tools-dock__surface--collapsed": isCollapsed,
        })}
      >
        <header className="ai-tools-dock__header">
          <div>
            <h2 className="ai-tools-dock__title">AI Tools</h2>
            <p className="ai-tools-dock__subtitle">
              Generate and drop outputs on the canvas without leaving the board.
            </p>
          </div>
          <div className="ai-tools-dock__actions">
            <button
              type="button"
              className="ai-tools-dock__toggle"
              onClick={handleToggleCollapse}
              aria-expanded={!isCollapsed}
              aria-label={isCollapsed ? "Expand AI tools" : "Collapse AI tools"}
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              <svg
                className="ai-tools-dock__toggle-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M6 10l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            <button
              type="button"
              className="ai-tools-dock__close"
              onClick={handleClosePanel}
              aria-label="Close AI tools"
            >
              {CloseIcon}
            </button>
          </div>
        </header>

        <div className="ai-tools-dock__body">
          <div
            className={clsx("ai-tools-panel", {
              "ai-tools-panel--enter": layoutAnimating,
            })}
          >
            <div className="ai-tools-panel__sidebar">
              <div className="ai-tools-panel__search-wrapper">
                <input
                  id={searchInputId}
                  type="text"
                  className="ai-tools-panel__search"
                  placeholder="Start typing to browse"
                  aria-label="Search AI tools"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={stopKeyboardPropagation}
                  onKeyUp={stopKeyboardPropagation}
                />
              </div>

              <div
                key={listAnimationKey}
                className={clsx("ai-tools-panel__list", {
                  "ai-tools-panel__list--animate": hasSearchQuery || !!search,
                  "ai-tools-panel__list--initial": layoutAnimating,
                })}
              >
                {!loadingTools && !toolsError && (
                  <p className="ai-tools-panel__list-headline">
                    {hasSearchQuery
                      ? `Showing ${visibleTools.length || 0} result${visibleTools.length === 1 ? "" : "s"}`
                      : `All tools`}
                  </p>
                )}
                {loadingTools && (
                  <div className="ai-tools-panel__placeholder">Loading AI tools…</div>
                )}
                {toolsError && (
                  <div className="ai-tools-panel__placeholder ai-tools-panel__placeholder--error">
                    <p>{toolsError}</p>
                    <button
                      type="button"
                      className="ai-tools-panel__button"
                      onClick={handleRetryLoadTools}
                    >
                      Retry
                    </button>
                  </div>
                )}
                {!loadingTools &&
                  !toolsError &&
                  !hasSearchQuery &&
                  visibleTools.length === 0 && (
                    <div className="ai-tools-panel__placeholder ai-tools-panel__placeholder--muted">
                      Pick a tool or type to search.
                    </div>
                  )}
                {!loadingTools && !toolsError && showNoResults && (
                  <div className="ai-tools-panel__placeholder">No tools match your search.</div>
                )}
                {!loadingTools &&
                  !toolsError &&
                  visibleTools.map((tool, index) => (
                    <button
                      key={tool.id}
                      type="button"
                      className={clsx("ai-tools-panel__tool", {
                        "ai-tools-panel__tool--selected": selectedTool?.id === tool.id,
                      })}
                      onClick={() => setSelectedToolId(tool.id)}
                      style={{ animationDelay: `${index * 40}ms` }}
                    >
                      <div className="ai-tools-panel__tool-head">
                        <span className="ai-tools-panel__tool-label">{tool.label}</span>
                        {tool.category && (
                          <span className="ai-tools-panel__badge">{tool.category}</span>
                        )}
                      </div>
                      {tool.description && (
                        <p className="ai-tools-panel__tool-description">{tool.description}</p>
                      )}
                      <div className="ai-tools-panel__tool-meta">
                        {tool.cost && <span>Tokens: {tool.cost}</span>}
                        {/* <span className="ai-tools-panel__endpoint">{tool.endpoint_id}</span> */}
                      </div>
                    </button>
                  ))}
              </div>
            </div>

            <div className="ai-tools-panel__content">
              <div
                key={selectedTool?.id ?? "placeholder"}
                className={clsx("ai-tools-panel__content-inner", {
                  "ai-tools-panel__content-inner--loaded": !!selectedTool,
                })}
              >
                {selectedTool ? (
                  <>
                  <header className="ai-tools-panel__header">
                    <div>
                      <h3>{selectedTool.label}</h3>
                      {selectedTool.description && (
                        <p className="ai-tools-panel__subtitle">{selectedTool.description}</p>
                      )}
                    </div>
                    {selectedTool.cost && (
                      <span className="ai-tools-panel__cost">{selectedTool.cost} / run</span>
                    )}
                  </header>


                  <form className="ai-tools-panel__form" onSubmit={handleGenerate}>
                    {(selectedTool.input_schema || []).map((field) => {
                      const value = fieldValues[field.key];

                      if (field.type === "file") {
                        const rawFiles = Array.isArray(value)
                          ? value
                          : typeof value === "string" && value
                            ? [value]
                            : [];
                        const files = rawFiles.filter(
                          (entry): entry is string =>
                            typeof entry === "string" && entry.length > 0,
                        );
                        return (
                          <label className="ai-tools-panel__form-field" key={field.key}>
                            <span>
                              {field.label}
                              {field.required && <sup>*</sup>}
                            </span>
                            <div className="ai-tools-panel__file-field">
                              <button
                                type="button"
                                className="ai-tools-panel__button"
                                onClick={() => handleOpenDrivePicker(field)}
                              >
                                Choose from Drive
                              </button>
                              {files.length > 0 && (
                                <ul className="ai-tools-panel__file-list">
                                  {files.map((fileUrl) => (
                                    <li key={fileUrl}>
                                      <a href={fileUrl} target="_blank" rel="noreferrer">
                                        {fileUrl.replace(/^https?:\/\//, "")}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setFieldValues((prev) => {
                                            const next = prev[field.key];
                                            if (Array.isArray(next)) {
                                              return {
                                                ...prev,
                                                [field.key]: next.filter((entry) => entry !== fileUrl),
                                              };
                                            }
                                            return { ...prev, [field.key]: "" };
                                          })
                                        }
                                      >
                                        Remove
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </label>
                        );
                      }

                      if (field.type === "boolean") {
                        return (
                          <label className="ai-tools-panel__checkbox" key={field.key}>
                            <input
                              type="checkbox"
                              checked={Boolean(value)}
                              onChange={(event) =>
                                setFieldValues((prev) => ({
                                  ...prev,
                                  [field.key]: event.target.checked,
                                }))
                              }
                            />
                            <span>
                              {field.label}
                              {field.required && <sup>*</sup>}
                            </span>
                          </label>
                        );
                      }

                      if (field.type === "select") {
                        const options = parseOptions(field.options);
                        return (
                          <label className="ai-tools-panel__form-field" key={field.key}>
                            <span>
                              {field.label}
                              {field.required && <sup>*</sup>}
                            </span>
                            <select
                              value={String(value ?? "")}
                              onChange={(event) =>
                                setFieldValues((prev) => ({
                                  ...prev,
                                  [field.key]: event.target.value,
                                }))
                              }
                              onKeyDown={stopKeyboardPropagation}
                              onKeyUp={stopKeyboardPropagation}
                            >
                              {options.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </label>
                        );
                      }

                      if (field.type === "textarea") {
                        return (
                          <label className="ai-tools-panel__form-field" key={field.key}>
                            <span>
                              {field.label}
                              {field.required && <sup>*</sup>}
                            </span>
                            <textarea
                              value={String(value ?? "")}
                              onChange={(event) =>
                                setFieldValues((prev) => ({
                                  ...prev,
                                  [field.key]: event.target.value,
                                }))
                              }
                              rows={4}
                              onKeyDown={stopKeyboardPropagation}
                              onKeyUp={stopKeyboardPropagation}
                            />
                          </label>
                        );
                      }

                      return (
                        <label className="ai-tools-panel__form-field" key={field.key}>
                          <span>
                            {field.label}
                            {field.required && <sup>*</sup>}
                          </span>
                          <input
                            type={field.type === "number" ? "number" : "text"}
                            value={String(value ?? "")}
                            onChange={(event) =>
                              setFieldValues((prev) => ({
                                ...prev,
                                [field.key]: event.target.value,
                              }))
                            }
                            onKeyDown={stopKeyboardPropagation}
                            onKeyUp={stopKeyboardPropagation}
                          />
                        </label>
                      );
                    })}

                    <div className="ai-tools-panel__actions">
                      <button
                        type="submit"
                        className="ai-tools-panel__button ai-tools-panel__button--primary"
                        disabled={
                          isGenerating ||
                          !selectedTool ||
                          hasMissingRequiredFields(selectedTool, fieldValues)
                        }
                      >
                        {isGenerating ? "Generating…" : "Generate"}
                      </button>
                      <button
                        type="button"
                        className="ai-tools-panel__button ai-tools-panel__button--ghost"
                        onClick={() =>
                          setFieldValues(() => {
                            const reset: Record<string, FieldValue> = {};
                            (selectedTool.input_schema || []).forEach((field) => {
                              reset[field.key] = normalizeFieldValue(field);
                            });
                            return reset;
                          })
                        }
                      >
                        Reset
                      </button>
                    </div>
                  </form>

                  {status && (
                    <div
                      className={clsx("ai-tools-panel__status", {
                        "ai-tools-panel__status--success": status.type === "success",
                        "ai-tools-panel__status--error": status.type === "error",
                      })}
                      role="status"
                    >
                      {status.message}
                    </div>
                  )}

                  {previews.length > 0 && (
                    <section className="ai-tools-panel__previews">
                      <h4>Latest outputs</h4>
                      <div className="ai-tools-panel__preview-grid">
                        {previews.map((url) => (
                          <a
                            href={url}
                            key={url}
                            target="_blank"
                            rel="noreferrer"
                            className="ai-tools-panel__preview"
                          >
                            <img src={url} alt="Generated asset preview" />
                          </a>
                        ))}
                      </div>
                    </section>
                  )}
                </>
                ) : (
                  <div className="ai-tools-panel__placeholder ai-tools-panel__placeholder--muted">
                    {loadingTools
                      ? "Loading AI tools…"
                      : showNoResults
                        ? "No tools match your search."
                        : toolsError
                          ? "AI tools are not available yet."
                          : "Search and select an AI tool to get started."}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>,
    portalContainer,
  );
};
